import { useEffect, useRef, useCallback, useState } from 'react';

const TIMEOUT_MS = 5 * 60 * 1000;
const WARNING_MS = 30 * 1000;
const THROTTLE_MS = 30 * 1000;

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'click', 'keydown', 'keyup', 'touchstart', 'touchmove', 'scroll', 'wheel'];

/**
 * Session timeout for onboarding flow.
 * Only activates when isActive=true (i.e. after the user has set a passcode / profile exists).
 * Does NOT call signOut — instead sets timedOut=true so the flow can show a session-expired screen.
 */
export function useOnboardingSessionTimeout(isActive: boolean) {
  const lastActivityRef = useRef(Date.now());
  const lastResetRef = useRef(0);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [showCountdown, setShowCountdown] = useState(false);
  const showCountdownRef = useRef(false);
  const [timedOut, setTimedOut] = useState(false);

  const clearTimers = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (warningRef.current) { clearTimeout(warningRef.current); warningRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  const doTimeout = useCallback(() => {
    clearTimers();
    setShowCountdown(false);
    showCountdownRef.current = false;
    setCountdownSeconds(0);
    setTimedOut(true);
  }, [clearTimers]);

  const scheduleTimers = useCallback((ms: number) => {
    clearTimers();
    setShowCountdown(false);
    showCountdownRef.current = false;
    setCountdownSeconds(0);

    warningRef.current = setTimeout(() => {
      setShowCountdown(true);
      showCountdownRef.current = true;
      setCountdownSeconds(30);
      countdownRef.current = setInterval(() => {
        setCountdownSeconds(prev => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
    }, ms - WARNING_MS);

    timerRef.current = setTimeout(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed < TIMEOUT_MS - 1000) {
        scheduleTimers(TIMEOUT_MS - elapsed);
        return;
      }
      doTimeout();
    }, ms);
  }, [clearTimers, doTimeout]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    lastResetRef.current = Date.now();
    scheduleTimers(TIMEOUT_MS);
  }, [scheduleTimers]);

  useEffect(() => {
    if (!isActive) {
      clearTimers();
      setShowCountdown(false);
      showCountdownRef.current = false;
      setCountdownSeconds(0);
      return;
    }

    lastActivityRef.current = Date.now();
    lastResetRef.current = Date.now();
    scheduleTimers(TIMEOUT_MS);

    const handler = () => {
      lastActivityRef.current = Date.now();
      if (showCountdownRef.current) {
        lastResetRef.current = Date.now();
        scheduleTimers(TIMEOUT_MS);
        return;
      }
      if (Date.now() - lastResetRef.current > THROTTLE_MS) {
        lastResetRef.current = Date.now();
        scheduleTimers(TIMEOUT_MS);
      }
    };

    ACTIVITY_EVENTS.forEach(e => document.addEventListener(e, handler, { passive: true }));

    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastActivityRef.current;
        if (elapsed >= TIMEOUT_MS) {
          clearTimers();
          setShowCountdown(false);
          showCountdownRef.current = false;
          setTimedOut(true);
        } else if (elapsed >= TIMEOUT_MS - WARNING_MS) {
          const remaining = TIMEOUT_MS - elapsed;
          clearTimers();
          setShowCountdown(true);
          showCountdownRef.current = true;
          setCountdownSeconds(Math.ceil(remaining / 1000));
          countdownRef.current = setInterval(() => {
            setCountdownSeconds(prev => (prev <= 1 ? 0 : prev - 1));
          }, 1000);
          timerRef.current = setTimeout(() => {
            clearTimers();
            setShowCountdown(false);
            showCountdownRef.current = false;
            setTimedOut(true);
          }, remaining);
        } else {
          lastResetRef.current = Date.now();
          scheduleTimers(TIMEOUT_MS - elapsed);
        }
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach(e => document.removeEventListener(e, handler));
      document.removeEventListener('visibilitychange', visibilityHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  return { showCountdown, countdownSeconds, resetTimer, timedOut };
}
